import { compareUint8Arrays, isUint8Array } from "uint8array-extras";
import {
  HashIdPreimage,
  HashIdPreimageSorobanAuthorization,
  HashIdPreimageSorobanAuthorizationWithAddress,
  Int64,
  ScAddress,
  ScVal,
  type ScValWire,
  SorobanAddressCredentials,
  SorobanAddressCredentialsWithDelegates,
  SorobanAuthorizationEntry,
  SorobanAuthorizedInvocation,
  SorobanCredentials,
  SorobanDelegateSignature,
} from "../xdr/index.js";

import { Keypair } from "./keypair.js";
import { StrKey } from "./strkey.js";

import type { Networks } from "./network.js";
import { hash } from "./hashing.js";

import { Address } from "./address.js";
import { nativeToScVal } from "./scval.js";

/**
 * A callback for signing an XDR structure representing all of the details
 * necessary to authorize an invocation tree.
 *
 * @param preimage - the entire authorization envelope whose hash you should
 *    sign, so that you can inspect the entire structure if necessary (rather
 *    than blindly signing a hash)
 * @param payload - the 32-byte signing payload, i.e. the sha256 hash of the
 *    preimage bytes (`hash(preimage.toXdr())`), provided as a convenience so
 *    you never have to re-derive it (e.g. for HSMs or remote signers that only
 *    accept a digest)
 *
 * @returns one of the following:
 *
 *  - the signature of the payload as a naked `Uint8Array`, implying it is
 *    signed by the key corresponding to `forAddress` when supplied to
 *    {@link authorizeEntry}, otherwise the entry's top-level credential
 *    address. Use the explicit `publicKey` form when the signing key differs
 *    from the target address,
 *  - an object with the `signature` alongside an explicit `publicKey` string
 *    identifying the Ed25519 signer, or
 *  - an object with a `signatureScVal`: an arbitrary, caller-built
 *    {@link xdr.ScVal} that is placed verbatim into the credentials'
 *    `signature` field. Use this for custom account contracts (smart wallets,
 *    passkey/WebAuthn signers, etc.) whose `__check_auth` expects a signature
 *    structure other than the built-in Stellar account
 *    `{public_key, signature}` vector. No Ed25519 verification is performed on
 *    this variant, and no `scvVec` wrapping is applied — you own the exact
 *    shape. The optional `address` selects which credential node receives the
 *    signature (like `forAddress` on {@link authorizeEntry}, which takes
 *    precedence if both are given).
 */
export type SigningCallback = (
  preimage: HashIdPreimage,
  payload: Uint8Array,
) => Promise<
  | Uint8Array
  | { signature: Uint8Array; publicKey: string }
  | { signatureScVal: ScVal; address?: string }
>;

/**
 * Returns `value` as an `xdr.ScVal`, or `null` if it isn't one. Accepts a value
 * built by a *different* copy of the SDK loaded in the same process (a dual
 * ESM/CJS load, or two installed versions), where `instanceof` fails on an
 * otherwise perfectly good value. Every arm class carries the union's own
 * schema name, so the brand covers `scvVec`, `scvMap` and the rest without
 * enumerating them.
 */
function toScVal(value: unknown): ScVal | null {
  if (ScVal.is(value)) return value;
  if (typeof value !== "object" || value === null) return null;
  const ctor = value.constructor as { schema?: { name?: string } } | undefined;
  if (ctor?.schema?.name !== ScVal.schema.name) return null;
  const toXdrObject = (value as { toXdrObject?: () => ScValWire }).toXdrObject;
  if (typeof toXdrObject !== "function") return null;
  // The brand is only what the value claims to be, and it is written into the
  // credentials to be read at serialization time — much later than the call
  // that produced it. Round-tripping the wire value through the *local* schema
  // is what proves it, and hands back a local instance rather than storing a
  // foreign object whose insides were never checked.
  try {
    return ScVal.fromXdr(ScVal.schema.encode(toXdrObject.call(value)));
  } catch {
    return null;
  }
}

/**
 * Actually authorizes an existing authorization entry using the given
 * credentials and expiration details, returning a signed copy.
 *
 * This "fills out" the authorization entry with a signature, indicating to the
 * {@link Operation.invokeHostFunction} its attached to that:
 *   - a particular identity (i.e. signing {@link Keypair} or other signer)
 *   - approving the execution of an invocation tree (i.e. a simulation-acquired
 *     {@link xdr.SorobanAuthorizedInvocation} or otherwise built)
 *   - on a particular network (uniquely identified by its passphrase, see
 *     {@link Networks})
 *   - until a particular ledger sequence is reached.
 *
 * This one lets you pass either a {@link Keypair} (or, more accurately,
 * anything with a `sign(Uint8Array): Uint8Array` method) or a callback function (see
 * {@link SigningCallback}) to handle signing the envelope hash.
 *
 * @param entry - an unsigned authorization entry
 * @param signer - either a {@link Keypair} instance or a function (see
 *    {@link SigningCallback}) which receives the
 *    {@link xdr.HashIdPreimage} input payload plus its 32-byte signing hash
 *    and returns EITHER
 *
 *      (a) an object containing a `signature` of the hash of the raw payload
 *          bytes as a `Uint8Array` and a `publicKey` string representing who just
 *          created this signature,
 *      (b) just the naked signature of the hash of the raw payload bytes (where
 *          the signing key is implied to be `forAddress` when supplied,
 *          otherwise the entry's top-level credential address), or
 *      (c) an object containing a `signatureScVal` — an arbitrary, caller-built
 *          {@link xdr.ScVal} written verbatim as the credentials' signature,
 *          for custom account contracts (smart wallets, passkey/WebAuthn
 *          signers) whose `__check_auth` expects a non-Ed25519 signature shape.
 *
 *    Option (b) is JUST for backwards compatibility and will be
 *    removed in the future.
 * @param validUntilLedgerSeq - the (exclusive) future ledger sequence number
 *    until which this authorization entry should be valid (if
 *    `currentLedgerSeq==validUntil`, this is expired)
 * @param networkPassphrase - the network passphrase is incorporated into the
 *    signature (see {@link Networks} for options)
 *
 * @param forAddress - which credential node the signature should be written
 *    to. Only relevant for `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES`, where
 *    a single entry can be signed by the top-level account and/or any of its
 *    (possibly nested) delegates. Per CAP-71-01 every one of these signers
 *    signs the *same* payload (bound to the top-level address), so the
 *    signature produced here is written to whichever node(s) carry
 *    `forAddress`. When omitted, the signature is written to the top-level
 *    credentials, which preserves the behavior for `SOROBAN_CREDENTIALS_ADDRESS`
 *    / `SOROBAN_CREDENTIALS_ADDRESS_V2` and for accounts whose signing key
 *    differs from the credential address (e.g. multisig). A bare signature is
 *    verified against this address.
 *
 * @see authorizeInvocation
 * @example
 * ```ts
 * import {
 *   rpc,
 *   Operation,
 *   Transaction,
 *   Networks,
 *   authorizeEntry
 * } from '@stellar/stellar-sdk';
 *
 * // Assume signPayloadCallback is a well-formed signing callback.
 * //
 * // It might, for example, pop up a modal from a browser extension, send the
 * // transaction to a third-party service for signing, or just do simple
 * // signing via Keypair like it does here:
 * async function signPayloadCallback(preimage, payload) {
 *    // `payload` is hash(preimage.toXdr()) — inspect `preimage` if you want
 *    // to display/verify what is being authorized before signing.
 *    return signer.sign(payload);
 * }
 *
 * async function multiPartyAuth(
 *    server: rpc.Server,
 *    // assume this involves multi-party auth
 *    tx: Transaction,
 * ) {
 *    const sim = await server.simulateTransaction(tx);
 *    if (!rpc.Api.isSimulationSuccess(sim)) {
 *      throw new Error('simulation failed');
 *    }
 *
 *    const invoke = tx.operations[0];
 *    if (invoke.type !== 'invokeHostFunction') {
 *      throw new Error('expected an invokeHostFunction operation');
 *    }
 *
 *    // Simulation supplies the entries to authorize. `authorizeEntry` returns
 *    // a signed *copy*, so collect the results rather than discarding them.
 *    const auth = await Promise.all(
 *      (sim.result?.auth ?? []).map((entry) =>
 *        authorizeEntry(
 *          entry,
 *          signPayloadCallback,
 *          currentLedger + 1000,
 *          Networks.TESTNET,
 *        ),
 *      ),
 *    );
 *
 *    // Replace the operation rather than mutating it: a built `Transaction`'s
 *    // operations must not be changed. `assembleTransaction` applies the
 *    // resources and fee from simulation, and keeps auth that is already
 *    // present. Call `build()` once — each call consumes a sequence number.
 *    const built = rpc.assembleTransaction(tx, sim)
 *      .clearOperations()
 *      .addOperation(Operation.invokeHostFunction({
 *        source: invoke.source,
 *        func: invoke.func,
 *        auth,
 *      }))
 *      .build();
 *
 *    built.sign(source);
 *    return server.sendTransaction(built);
 * }
 * ```
 */
export async function authorizeEntry(
  entry: SorobanAuthorizationEntry,
  signer: Keypair | SigningCallback,
  validUntilLedgerSeq: number,
  networkPassphrase: string,
  forAddress?: string,
): Promise<SorobanAuthorizationEntry> {
  // no-op if it's source account auth
  if (entry.credentials.type === "sorobanCredentialsSourceAccount") {
    return entry;
  }

  // XDR values are immutable, so the input entry can be read directly — the
  // result below is always a freshly-built entry, never a mutation of it.
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null) {
    // We should have already returned if the credentials were source account
    // credentials, so if we can't get address credentials out of this, it's an
    // unsupported credential type.
    throw new Error(`unsupported credential type ${credentials.type}`);
  }

  // Checked before signing, so a wallet isn't prompted for a discarded signature.
  if (
    forAddress !== undefined &&
    !collectSignatureNodes(credentials).some(
      (node) => Address.fromScAddress(node.address).toString() === forAddress,
    )
  ) {
    throw new Error(
      `the authorization entry has no credential node for address ${forAddress}`,
    );
  }

  // The preimage commits to the (updated) expiration ledger, so build it with
  // `validUntilLedgerSeq` directly; the same value gets written back onto the
  // credentials below, keeping the signed hash and the stored expiration in
  // sync. Otherwise the network reconstructs the preimage from the updated
  // credentials and the signature no longer matches.
  const preimage = buildAuthorizationEntryPreimage(
    entry,
    validUntilLedgerSeq,
    networkPassphrase,
  );

  const payload = hash(preimage.toXdr());

  let signatureScVal: ScVal;
  let targetAddress = forAddress;
  let sigResult: Awaited<ReturnType<SigningCallback>> | null = null;
  if (typeof signer === "function") {
    // Hand the callback its own copy of the payload: `payload` is what the
    // Ed25519 signature is verified against below, so a callback mutating the
    // array it received must not be able to shift what "valid" means.
    sigResult = await signer(preimage, Uint8Array.from(payload));
  }

  if (
    sigResult !== null &&
    typeof sigResult === "object" &&
    "signatureScVal" in sigResult
  ) {
    // Custom-credential path (smart wallets, passkeys/WebAuthn, etc.): the
    // caller owns the exact ScVal their account contract's `__check_auth`
    // expects, so it is written verbatim — no Ed25519 verification and no
    // scvVec wrapping. Nothing downstream inspects it, so it is checked here or
    // not at all.
    const candidate: unknown = sigResult.signatureScVal;
    const asScVal = toScVal(candidate);
    if (asScVal === null) {
      throw new TypeError(
        `signatureScVal must be an xdr.ScVal, got ${candidate === null ? "null" : typeof candidate}`,
      );
    }
    signatureScVal = asScVal;
    targetAddress ??= sigResult.address;
  } else {
    let signature: unknown;
    let publicKey: unknown;
    if (typeof signer === "function") {
      if (
        sigResult !== null &&
        typeof sigResult === "object" &&
        "signature" in sigResult
      ) {
        signature = sigResult.signature;
        publicKey = sigResult.publicKey;
      } else if (isUint8Array(sigResult)) {
        signature = sigResult;
        publicKey =
          targetAddress ?? Address.fromScAddress(addrAuth.address).toString();
      } else {
        // Without this the value reached `verify` unchecked. A wrong shape that
        // still carries a signature (an `xdr.Signature` wrapper) got a forgery
        // verdict for a valid signature; one that carries none surfaces
        // `verify`'s own TypeError, which names a parameter the caller never
        // passed.
        throw new TypeError(
          "SigningCallback must resolve to a Uint8Array, " +
            "{ signature, publicKey }, or { signatureScVal }; got " +
            `${sigResult === null ? "null" : typeof sigResult}`,
        );
      }
    } else {
      signature = signer.sign(payload);
      publicKey = signer.publicKey();
    }

    // `signature` and `publicKey` arrive from three places above — two callback
    // shapes and a signer object — so they are checked here, where those paths
    // meet, rather than in each branch.
    if (typeof publicKey !== "string") {
      throw new TypeError(
        `expected a public key string from the signer, got ${typeof publicKey}`,
      );
    }
    if (!isUint8Array(signature)) {
      throw new TypeError(
        `expected a Uint8Array signature from the signer, got ${signature === null ? "null" : typeof signature}`,
      );
    }

    // A contract address has no Ed25519 key; name the remedy instead of
    // surfacing StrKey's "invalid version byte".
    if (!StrKey.isValidEd25519PublicKey(publicKey)) {
      throw new TypeError(
        StrKey.isValidContract(publicKey)
          ? `cannot verify an Ed25519 signature against contract ${publicKey}; ` +
              "sign for a contract address with a callback returning " +
              "{ signatureScVal }, or with a custom `authorizeEntry` when " +
              "signing through signAuthEntries"
          : "expected an Ed25519 public key (G...) to verify the signature " +
              `against, got ${JSON.stringify(publicKey)}`,
      );
    }

    if (!Keypair.fromPublicKey(publicKey).verify(payload, signature)) {
      throw new Error(`signature doesn't match payload`);
    }

    // This structure is defined here:
    // https://soroban.stellar.org/docs/fundamentals-and-concepts/invoking-contracts-with-transactions#stellar-account-signatures
    //
    // Encoding a contract structure as an ScVal means the map keys are supposed
    // to be symbols, hence the forced typing here.
    const sigScVal = nativeToScVal(
      {
        public_key: StrKey.decodeEd25519PublicKey(publicKey),
        signature,
      },
      {
        type: {
          public_key: ["symbol", null],
          signature: ["symbol", null],
        },
      },
    );

    signatureScVal = ScVal.scvVec([sigScVal]);
  }

  // CAP-71-01: the signature payload is shared across the top-level address and
  // every (possibly nested) delegate, so this signer's signature is written to
  // whichever credential node(s) carry `targetAddress`. When no target is given
  // we fall back to the top-level credentials, which preserves the behavior for
  // ADDRESS / ADDRESS_V2 and for accounts whose signing key differs from the
  // credential address (e.g. multisig). Because the class-XDR types are
  // immutable, the updated expiration and signature are folded into a freshly
  // built credential tree.
  const { credentials: signedCredentials, matched } =
    applyExpirationAndSignature(
      credentials,
      validUntilLedgerSeq,
      signatureScVal,
      targetAddress,
    );

  if (matched === 0) {
    throw new Error(
      `the authorization entry has no credential node for address ${targetAddress}`,
    );
  }

  return new SorobanAuthorizationEntry({
    credentials: signedCredentials,
    rootInvocation: entry.rootInvocation,
  });
}

/**
 * This builds an entry from scratch, allowing you to express authorization as a
 * function of:
 *   - a particular identity (i.e. signing {@link Keypair} or other signer)
 *   - approving the execution of an invocation tree (i.e. a simulation-acquired
 *     {@link xdr.SorobanAuthorizedInvocation} or otherwise built)
 *   - on a particular network (uniquely identified by its passphrase, see
 *     {@link Networks})
 *   - until a particular ledger sequence is reached.
 *
 * This is in contrast to {@link authorizeEntry}, which signs an existing entry.
 *
 * @param params - the parameters for building and signing the authorization
 *   - `signer`: either a {@link Keypair} instance (or anything with a
 *    `.sign(buf): Uint8Array` method) or a function which takes a payload (a
 *    {@link xdr.HashIdPreimageSorobanAuthorization} instance) input and returns
 *    the signature of the hash of the raw payload bytes (where the signing key
 *    should correspond to the address in the `entry`)
 *   - `validUntilLedgerSeq`: the (exclusive) future ledger sequence
 *    number until which this authorization entry should be valid (if
 *    `currentLedgerSeq==validUntilLedgerSeq`, this is expired)
 *   - `invocation`: the invocation tree that we're authorizing
 *    (likely, this comes from transaction simulation)
 *   - `networkPassphrase`: the network passphrase is incorporated into
 *    the signature (see {@link Networks} for options)
 *   - `publicKey`: the public identity of the signer (when providing a
 *    {@link Keypair} to `signer`, this can be omitted, as it just uses
 *    {@link Keypair.publicKey})
 *   - `authV2`: build `SOROBAN_CREDENTIALS_ADDRESS_V2` (CAP-71) credentials
 *    rather than the legacy `SOROBAN_CREDENTIALS_ADDRESS`. Defaults to `true`;
 *    pass `false` for the legacy format.
 *
 * @see authorizeEntry
 */
export interface AuthorizeInvocationParams {
  signer: Keypair | SigningCallback;
  validUntilLedgerSeq: number;
  invocation: SorobanAuthorizedInvocation;
  networkPassphrase: string;
  publicKey?: string;
  /**
   * Build `SOROBAN_CREDENTIALS_ADDRESS_V2` (CAP-71) credentials instead of the
   * legacy `SOROBAN_CREDENTIALS_ADDRESS`. V2 credentials bind the address into
   * the signed payload.
   * @defaultValue true
   */
  authV2?: boolean;
}

export function authorizeInvocation(
  params: AuthorizeInvocationParams,
): Promise<SorobanAuthorizationEntry> {
  const {
    signer,
    validUntilLedgerSeq,
    invocation,
    networkPassphrase,
    publicKey = "",
    authV2 = true,
  } = params;
  // We use keypairs as a source of randomness for the nonce to avoid mucking
  // with any crypto dependencies. Note that this just has to be random and
  // unique, not cryptographically secure, so it's fine.
  const kp = Keypair.random().rawPublicKey();
  const nonce = Int64(bytesToInt64(kp));

  const pk =
    publicKey || (signer instanceof Keypair ? signer.publicKey() : null);
  if (!pk) {
    throw new Error(`authorizeInvocation requires publicKey parameter`);
  }

  // V1 and V2 carry the identical SorobanAddressCredentials payload; only the
  // credential union arm differs. authorizeEntry picks the matching signature
  // preimage (legacy vs. address-bound) off whichever arm we build here.
  const addressCredentials = new SorobanAddressCredentials({
    address: new Address(pk).toScAddress(),
    nonce,
    signatureExpirationLedger: 0, // replaced
    signature: ScVal.scvVec([]), // replaced
  });

  const entry = new SorobanAuthorizationEntry({
    rootInvocation: invocation,
    credentials: authV2
      ? SorobanCredentials.sorobanCredentialsAddressV2(addressCredentials)
      : SorobanCredentials.sorobanCredentialsAddress(addressCredentials),
  });

  return authorizeEntry(entry, signer, validUntilLedgerSeq, networkPassphrase);
}

/**
 * Builds the {@link xdr.HashIdPreimage} whose hash a signer must sign to
 * authorize `entry`. This is the low-level signature payload used by
 * {@link authorizeEntry}, exposed for callers that drive signing themselves —
 * most notably for `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES`, where the
 * client (not simulation) decides which delegates sign and how.
 *
 * For `SOROBAN_CREDENTIALS_ADDRESS` this is the legacy, non-address-bound
 * `ENVELOPE_TYPE_SOROBAN_AUTHORIZATION` preimage. For `SOROBAN_CREDENTIALS_ADDRESS_V2`
 * and `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES` it is the address-bound
 * `ENVELOPE_TYPE_SOROBAN_AUTHORIZATION_WITH_ADDRESS` preimage (CAP-71). For the
 * delegates variant this single payload — bound to the *top-level* address — is
 * what the top-level account and every (nested) delegate each sign.
 *
 * To get the raw bytes to sign, hash the XDR: `hash(preimage.toXdr())`.
 *
 * @param entry - the authorization entry to build the payload for
 * @param validUntilLedgerSeq - the expiration ledger committed into the payload
 *    (must match the `signatureExpirationLedger` on the credentials you submit)
 * @param networkPassphrase - the network passphrase mixed into the payload
 * @throws `Error` if `entry` carries source-account or otherwise non-address
 *    credentials
 */
export function buildAuthorizationEntryPreimage(
  entry: SorobanAuthorizationEntry,
  validUntilLedgerSeq: number,
  networkPassphrase: string,
): HashIdPreimage {
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null) {
    throw new Error(
      `cannot build a signature payload for credential type ${credentials.type}`,
    );
  }

  const networkId = hash(networkPassphrase);

  switch (credentials.type) {
    // legacy address credentials are not address-bound
    case "sorobanCredentialsAddress":
      return HashIdPreimage.envelopeTypeSorobanAuthorization(
        new HashIdPreimageSorobanAuthorization({
          networkId,
          nonce: addrAuth.nonce,
          invocation: entry.rootInvocation,
          signatureExpirationLedger: validUntilLedgerSeq,
        }),
      );

    // ADDRESS_V2 and ADDRESS_WITH_DELEGATES bind the address into the signed
    // payload via the WithAddress preimage (CAP-71)
    case "sorobanCredentialsAddressV2":
    case "sorobanCredentialsAddressWithDelegates":
      return HashIdPreimage.envelopeTypeSorobanAuthorizationWithAddress(
        new HashIdPreimageSorobanAuthorizationWithAddress({
          networkId,
          nonce: addrAuth.nonce,
          invocation: entry.rootInvocation,
          address: addrAuth.address,
          signatureExpirationLedger: validUntilLedgerSeq,
        }),
      );

    default:
      throw new Error(`unsupported credential type ${credentials.type}`);
  }
}

/**
 * A delegate signer to attach to a
 * `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES` entry via
 * {@link buildWithDelegatesEntry}.
 */
export interface DelegateSignature {
  /** the delegate's address (`G…` account or `C…` contract). */
  address: string;
  /**
   * the delegate's signature value. Defaults to a `scvVoid` placeholder, which
   * you can fill afterwards with {@link authorizeEntry} (passing this address
   * as `forAddress`) or by editing the entry directly.
   */
  signature?: ScVal;
  /** signers this delegate in turn delegates to (recursive). */
  nestedDelegates?: DelegateSignature[];
}

/** Parameters for {@link buildWithDelegatesEntry}. */
export interface BuildWithDelegatesParams {
  /**
   * an existing `SOROBAN_CREDENTIALS_ADDRESS` or
   * `SOROBAN_CREDENTIALS_ADDRESS_V2` entry — typically one returned by
   * simulation — whose address credentials should be wrapped.
   */
  entry: SorobanAuthorizationEntry;
  /** the expiration ledger sequence stored on the top-level credentials. */
  validUntilLedgerSeq: number;
  /** the delegate signers to attach. */
  delegates: DelegateSignature[];
  /**
   * the top-level account's signature. Defaults to `scvVoid`, which is valid
   * for accounts that authorize purely via delegated signers (CAP-71-01).
   */
  signature?: ScVal;
}

/**
 * Builds a `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES` authorization entry by
 * wrapping the address credentials of an existing `ADDRESS`/`ADDRESS_V2` entry
 * (e.g. one returned by simulation) together with a caller-provided set of
 * delegate signers.
 *
 * Simulation never emits the delegates variant on its own — which accounts use
 * delegated authentication is account-specific policy known only to the client
 * (much like a multisig policy). This helper just assembles the wrapper XDR;
 * you supply the delegate tree (addresses and, optionally, signatures). To
 * produce the signatures, build the shared payload with
 * {@link buildAuthorizationEntryPreimage} on the returned entry and sign it,
 * or fill each node afterwards with {@link authorizeEntry} (passing the
 * signer's address as `forAddress`).
 *
 * Each delegates array (the top-level set and every `nestedDelegates`) is
 * sorted by address in ascending order, and duplicate addresses within an array
 * are rejected, as the protocol requires (CAP-71-01) — otherwise the host
 * rejects the entry.
 *
 * @param params - see {@link BuildWithDelegatesParams}
 * @throws `Error` if `entry` is not an `ADDRESS`/`ADDRESS_V2` entry, or if any
 *    delegates array contains a duplicate address.
 */
export function buildWithDelegatesEntry(
  params: BuildWithDelegatesParams,
): SorobanAuthorizationEntry {
  const { entry, validUntilLedgerSeq, delegates, signature } = params;
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  if (
    addrAuth === null ||
    credentials.type === "sorobanCredentialsAddressWithDelegates"
  ) {
    throw new Error(
      `buildWithDelegatesEntry expects ADDRESS or ADDRESS_V2 credentials, got ${credentials.type}`,
    );
  }

  return new SorobanAuthorizationEntry({
    rootInvocation: entry.rootInvocation,
    credentials: SorobanCredentials.sorobanCredentialsAddressWithDelegates(
      new SorobanAddressCredentialsWithDelegates({
        addressCredentials: new SorobanAddressCredentials({
          address: addrAuth.address,
          nonce: addrAuth.nonce,
          signatureExpirationLedger: validUntilLedgerSeq,
          signature: signature ?? ScVal.scvVoid(),
        }),
        delegates: buildDelegateNodes(delegates),
      }),
    ),
  });
}

/**
 * Recursively converts {@link DelegateSignature} descriptors into
 * {@link xdr.SorobanDelegateSignature} nodes, sorting each level by address and
 * rejecting duplicates (CAP-71-01).
 */
function buildDelegateNodes(
  delegates: DelegateSignature[],
): SorobanDelegateSignature[] {
  const nodes = delegates.map(
    (delegate) =>
      new SorobanDelegateSignature({
        address: new Address(delegate.address).toScAddress(),
        signature: delegate.signature ?? ScVal.scvVoid(),
        nestedDelegates: buildDelegateNodes(delegate.nestedDelegates ?? []),
      }),
  );

  nodes.sort((a, b) =>
    compareUint8Arrays(a.address.toXdr(), b.address.toXdr()),
  );

  for (let i = 1; i < nodes.length; i++) {
    if (
      compareUint8Arrays(
        nodes[i - 1].address.toXdr(),
        nodes[i].address.toXdr(),
      ) === 0
    ) {
      throw new Error(
        `duplicate delegate address ${Address.fromScAddress(
          nodes[i].address,
        ).toString()}`,
      );
    }
  }

  return nodes;
}

/**
 * Internal helper — intentionally NOT re-exported from `base/index.js`, so it
 * is not part of the public SDK API. Shared with the contract package, which
 * imports it directly from this module. If a public need arises, add it to the
 * explicit auth re-exports in `base/index.ts`.
 *
 * Extracts the {@link xdr.SorobanAddressCredentials} from any address-based
 * Soroban credential, regardless of which credential type variant is used.
 *
 * This unifies access across `SOROBAN_CREDENTIALS_ADDRESS`,
 * `SOROBAN_CREDENTIALS_ADDRESS_V2` (which carries identical fields but binds
 * the address into the signature payload), and
 * `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES` (which wraps the same address
 * credentials alongside a set of delegate signatures).
 *
 * @param credentials - the credentials to inspect
 * @returns the inner address credentials, or `null` for source-account
 *    credentials (which carry no address payload)
 */
export function getAddressCredentials(
  credentials: SorobanCredentials,
): SorobanAddressCredentials | null {
  switch (credentials.type) {
    case "sorobanCredentialsAddress":
      return credentials.address;
    case "sorobanCredentialsAddressV2":
      return credentials.addressV2;
    case "sorobanCredentialsAddressWithDelegates":
      return credentials.addressWithDelegates.addressCredentials;
    default:
      return null;
  }
}

/**
 * Internal helper. Rebuilds an address-based credential with the signature
 * expiration ledger set on its top-level address credentials and this signer's
 * signature written onto whichever node(s) match `forAddress`.
 *
 * Because the class-XDR types are immutable, this constructs a fresh credential
 * tree rather than mutating in place. Per CAP-71-01 every signature-bearing
 * node commits to the same payload (bound to the top-level address), so the
 * caller can fill any of them with a signature produced from that shared
 * payload. When `forAddress` is omitted, only the top-level address credentials
 * receive the signature.
 *
 * @returns the rebuilt credentials and the number of nodes that received the
 *    signature (`0` means `forAddress` matched no node).
 */
function applyExpirationAndSignature(
  credentials: SorobanCredentials,
  validUntilLedgerSeq: number,
  signature: ScVal,
  forAddress: string | undefined,
): { credentials: SorobanCredentials; matched: number } {
  const topAddr = getAddressCredentials(credentials);
  if (topAddr === null) {
    return { credentials, matched: 0 };
  }

  let matched = 0;
  const topIsTarget =
    forAddress === undefined ||
    Address.fromScAddress(topAddr.address).toString() === forAddress;
  if (topIsTarget) {
    matched++;
  }

  const newTopAddr = new SorobanAddressCredentials({
    address: topAddr.address,
    nonce: topAddr.nonce,
    signatureExpirationLedger: validUntilLedgerSeq,
    signature: topIsTarget ? signature : topAddr.signature,
  });

  switch (credentials.type) {
    case "sorobanCredentialsAddress":
      return {
        credentials: SorobanCredentials.sorobanCredentialsAddress(newTopAddr),
        matched,
      };
    case "sorobanCredentialsAddressV2":
      return {
        credentials: SorobanCredentials.sorobanCredentialsAddressV2(newTopAddr),
        matched,
      };
    case "sorobanCredentialsAddressWithDelegates": {
      const withDelegates = credentials.addressWithDelegates;
      const newDelegates =
        forAddress === undefined
          ? withDelegates.delegates
          : rebuildDelegatesWithSignature(
              withDelegates.delegates,
              forAddress,
              signature,
              () => {
                matched++;
              },
            );
      return {
        credentials: SorobanCredentials.sorobanCredentialsAddressWithDelegates(
          new SorobanAddressCredentialsWithDelegates({
            addressCredentials: newTopAddr,
            delegates: newDelegates,
          }),
        ),
        matched,
      };
    }
    default:
      return { credentials, matched };
  }
}

/**
 * Internal helper. Recursively rebuilds a delegate tree, writing `signature`
 * onto every (possibly nested) node whose address matches `forAddress` and
 * invoking `onMatch` for each one, leaving all other nodes untouched.
 */
function rebuildDelegatesWithSignature(
  delegates: SorobanDelegateSignature[],
  forAddress: string,
  signature: ScVal,
  onMatch: () => void,
): SorobanDelegateSignature[] {
  return delegates.map((delegate) => {
    const isMatch =
      Address.fromScAddress(delegate.address).toString() === forAddress;
    if (isMatch) {
      onMatch();
    }
    return new SorobanDelegateSignature({
      address: delegate.address,
      signature: isMatch ? signature : delegate.signature,
      nestedDelegates: rebuildDelegatesWithSignature(
        delegate.nestedDelegates,
        forAddress,
        signature,
        onMatch,
      ),
    });
  });
}

/** The credential arm of a {@link xdr.SorobanAuthorizationEntry}. */
export type AuthEntryCredentialType =
  "sourceAccount" | "address" | "addressV2" | "addressWithDelegates";

/**
 * A single ed25519 signature parsed out of a credential node's signature
 * value, in the map format written by {@link authorizeEntry}.
 */
export interface AuthEntrySignature {
  /** the signer's public key, as a `G…` strkey. */
  publicKey: string;
  /** the raw 64-byte ed25519 signature. */
  signature: Uint8Array;
}

/**
 * One node of an authorization entry that can carry a signature: the top-level
 * address credentials and, for `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES`,
 * each (possibly nested) delegate.
 */
export interface AuthEntrySigner {
  /** the node's address (`G…` account or `C…` contract). */
  address: string;
  /**
   * whether a signature payload is present on this node (i.e. its signature is
   * neither `scvVoid` nor an empty `scvVec`). For contract (`C…`) addresses
   * this only means *something* is attached — whether it satisfies the
   * contract's `__check_auth` cannot be verified client-side.
   */
  signed: boolean;
  /**
   * the signature payload parsed as the SDK's standard ed25519 format (a vec
   * of `{public_key, signature}` maps, see {@link authorizeEntry}), or `null`
   * when the payload has some other, signer-defined shape (as custom accounts
   * such as WebAuthn/passkey wallets use). Of the two unsigned placeholder
   * forms, an empty `scvVec` parses as `[]` while `scvVoid` (not a vec at all)
   * parses as `null` — check `signed` rather than this field to tell whether a
   * node is unsigned.
   */
  signatures: AuthEntrySignature[] | null;
  /** the raw signature value, whatever its shape. */
  rawSignature: ScVal;
}

/**
 * A structured, read-only view of a {@link xdr.SorobanAuthorizationEntry},
 * returned by {@link inspectAuthEntry}.
 */
export interface AuthEntryInfo {
  credentialType: AuthEntryCredentialType;
  /** the authorizing address, or `null` for source-account credentials. */
  address: string | null;
  /** the credential nonce, or `null` for source-account credentials. */
  nonce: bigint | null;
  /**
   * the (exclusive) ledger sequence until which the signature is valid, or
   * `null` for source-account credentials. Note that unsigned entries commonly
   * carry a placeholder (often `0`) until {@link authorizeEntry} sets it.
   */
  signatureExpirationLedger: number | null;
  /**
   * every node that can carry a signature: the top-level credentials first,
   * then (for the delegates variant) each delegate, depth-first. Empty for
   * source-account credentials.
   */
  signers: AuthEntrySigner[];
  /**
   * whether every signer node carries a signature payload. Always `false` for
   * source-account credentials (which have no signature nodes — they are
   * instead covered by the transaction envelope signature; use
   * {@link checkAuthEntryReadiness} for a submit check). For the delegates
   * variant note that an account's policy may accept an unsigned top-level
   * node when its delegates have signed (CAP-71-01) — consult `signers` if you
   * support that.
   */
  signed: boolean;
  /** the invocation tree this entry authorizes. */
  invocation: SorobanAuthorizedInvocation;
}

/** The result of {@link checkAuthEntryReadiness}. */
export interface AuthEntryReadiness {
  /** `true` when the entry is fully signed and not expired. */
  ready: boolean;
  /**
   * `true` when `currentLedgerSeq >= signatureExpirationLedger` (expiration is
   * exclusive). Always `false` for source-account credentials.
   */
  expired: boolean;
  /** addresses of signer nodes that carry no signature payload. */
  unsignedBy: string[];
}

/**
 * Decodes a {@link xdr.SorobanAuthorizationEntry} into a plain, typed summary:
 * which credential variant it uses, which address authorizes it, its nonce and
 * expiration ledger, and — for every node that can carry a signature (the
 * top-level credentials plus any CAP-71 delegates) — whether it is signed and,
 * when the payload uses the SDK's standard ed25519 format, by which keys.
 *
 * This is the read-side complement to {@link authorizeEntry} /
 * {@link authorizeInvocation}: those fill entries with signatures, this
 * inspects what an entry (e.g. one returned by transaction simulation, or
 * received from a counterparty in a multi-party signing flow) requires and
 * already carries, without reaching into raw XDR accessors.
 *
 * @param entry - the authorization entry to inspect
 * @returns a {@link AuthEntryInfo} summary of the entry
 *
 * @see checkAuthEntryReadiness
 * @example
 * ```ts
 * const info = inspectAuthEntry(entry);
 * if (!info.signed && info.address !== null) {
 *   console.log(`${info.address} still needs to sign`, info.signers);
 * }
 * ```
 */
export function inspectAuthEntry(
  entry: SorobanAuthorizationEntry,
): AuthEntryInfo {
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);

  let credentialType: AuthEntryCredentialType;
  switch (credentials.type) {
    case "sorobanCredentialsSourceAccount":
      credentialType = "sourceAccount";
      break;
    case "sorobanCredentialsAddress":
      credentialType = "address";
      break;
    case "sorobanCredentialsAddressV2":
      credentialType = "addressV2";
      break;
    case "sorobanCredentialsAddressWithDelegates":
      credentialType = "addressWithDelegates";
      break;
    default:
      throw new Error(
        `unsupported credential type ${(credentials as SorobanCredentials).type}`,
      );
  }

  const signers = collectSignatureNodes(credentials).map(
    (node): AuthEntrySigner => ({
      address: Address.fromScAddress(node.address).toString(),
      signed: signaturePresent(node.signature),
      signatures: parseEd25519Signatures(node.signature),
      rawSignature: node.signature,
    }),
  );

  return {
    credentialType,
    address:
      addrAuth === null
        ? null
        : Address.fromScAddress(addrAuth.address).toString(),
    nonce: addrAuth === null ? null : addrAuth.nonce,
    signatureExpirationLedger:
      addrAuth === null ? null : addrAuth.signatureExpirationLedger,
    signers,
    signed: signers.length > 0 && signers.every((signer) => signer.signed),
    invocation: entry.rootInvocation,
  };
}

/**
 * Internal helper. One signature-carrying node, read out of a credential tree:
 * the top-level address credentials plus, for the CAP-71 delegates variant,
 * every (possibly nested) delegate, depth-first.
 */
interface SignatureNode {
  address: ScAddress;
  signature: ScVal;
}

/**
 * Internal helper. Collects every signature-carrying node of a credential tree,
 * read-only — the write-side counterpart is
 * {@link applyExpirationAndSignature}.
 */
function collectSignatureNodes(
  credentials: SorobanCredentials,
): SignatureNode[] {
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null) {
    return [];
  }

  const nodes: SignatureNode[] = [
    { address: addrAuth.address, signature: addrAuth.signature },
  ];

  if (credentials.type === "sorobanCredentialsAddressWithDelegates") {
    const walk = (delegates: SorobanDelegateSignature[]): void => {
      delegates.forEach((delegate) => {
        nodes.push({
          address: delegate.address,
          signature: delegate.signature,
        });
        walk(delegate.nestedDelegates);
      });
    };
    walk(credentials.addressWithDelegates.delegates);
  }

  return nodes;
}

/**
 * Reports whether an authorization entry is ready to submit at a given ledger:
 * fully signed and not yet expired.
 *
 * Source-account entries are always ready — they carry no signature or
 * expiration of their own and are instead covered by the transaction envelope
 * signature.
 *
 * The current ledger sequence is taken as a parameter (fetch it from a source
 * you trust, e.g. `rpc.Server.getLatestLedger`) rather than looked up here, so
 * this stays a pure decode with no network dependency.
 *
 * For `SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES`, this conservatively
 * requires *every* node (top-level and all delegates) to be signed. An
 * account's policy may accept an unsigned top-level node when its delegates
 * have signed (CAP-71-01); if you support that, check
 * {@link inspectAuthEntry}'s `signers` yourself.
 *
 * @param entry - the authorization entry to check
 * @param currentLedgerSeq - the network's current ledger sequence, compared
 *    (exclusively) against the entry's `signatureExpirationLedger`
 * @returns a {@link AuthEntryReadiness}: `ready`, `expired`, and which
 *    addresses are still `unsignedBy`
 * @throws `Error` if `currentLedgerSeq` cannot represent a uint32 ledger
 *    sequence (non-integer, negative, or above 2^32 - 1), which would make the
 *    expiration comparison unreliable
 */
export function checkAuthEntryReadiness(
  entry: SorobanAuthorizationEntry,
  currentLedgerSeq: number,
): AuthEntryReadiness {
  if (
    !Number.isInteger(currentLedgerSeq) ||
    currentLedgerSeq < 0 ||
    currentLedgerSeq > 0xffffffff
  ) {
    throw new Error(
      `currentLedgerSeq must be a uint32 ledger sequence, got ${currentLedgerSeq}`,
    );
  }

  const info = inspectAuthEntry(entry);
  if (info.credentialType === "sourceAccount") {
    return { ready: true, expired: false, unsignedBy: [] };
  }

  const expired = currentLedgerSeq >= (info.signatureExpirationLedger ?? 0);
  const unsignedBy = info.signers
    .filter((signer) => !signer.signed)
    .map((signer) => signer.address);

  return { ready: !expired && unsignedBy.length === 0, expired, unsignedBy };
}

/**
 * Internal helper. A node counts as unsigned when its signature is `scvVoid`
 * (the delegate/CAP-71 placeholder) or an empty `scvVec` (the placeholder
 * {@link authorizeInvocation} writes); anything else is a signature payload.
 */
export function signaturePresent(signature: ScVal): boolean {
  switch (signature.type) {
    case "scvVoid":
      return false;
    case "scvVec":
      return (signature.value ?? []).length > 0;
    default:
      return true;
  }
}

/**
 * Internal helper. Parses a signature value in the SDK's standard format — an
 * `scvVec` of `scvMap`s with symbol keys `public_key` (32 raw ed25519 key
 * bytes) and `signature` — back into typed pairs. Returns `null` when the
 * value has any other shape (e.g. a custom account's signer-defined payload).
 */
function parseEd25519Signatures(signature: ScVal): AuthEntrySignature[] | null {
  if (signature.type !== "scvVec") {
    return null;
  }

  const parsed: AuthEntrySignature[] = [];
  for (const element of signature.value ?? []) {
    if (element.type !== "scvMap") {
      return null;
    }
    let publicKey: Uint8Array | null = null;
    let sig: Uint8Array | null = null;
    for (const mapEntry of element.value ?? []) {
      const { key, val } = mapEntry;
      if (key.type !== "scvSymbol" || val.type !== "scvBytes") {
        return null;
      }
      switch (key.value.toString()) {
        case "public_key":
          publicKey = val.value.value;
          break;
        case "signature":
          sig = val.value.value;
          break;
        default:
          return null;
      }
    }
    if (
      publicKey === null ||
      sig === null ||
      publicKey.length !== 32 ||
      sig.length !== 64
    ) {
      return null;
    }
    parsed.push({
      publicKey: StrKey.encodeEd25519PublicKey(publicKey),
      signature: sig,
    });
  }

  return parsed;
}

function bytesToInt64(bytes: Uint8Array): bigint {
  const buf = bytes.subarray(0, 8);
  if (buf.length < 8) {
    throw new Error(
      `need at least 8 bytes to convert to Int64, got ${bytes.length}`,
    );
  }
  const view = new DataView(buf.buffer, buf.byteOffset, 8);
  const value = view.getBigInt64(0, false);

  return value;
}
